import { v4 as uuidv4 } from 'uuid';
import type {
  ReceiptSettings,
  HeaderConfig,
  TwoColumnConfig,
  ItemsListConfig,
  TotalsConfig,
  PaymentConfig,
  DateTimeConfig,
  CustomMessageConfig,
  BarcodeConfig,
  DividerConfig,
  ReceiptSection,
  SectionType,
  ReceiptTemplate,
} from './schema';

// Default Receipt Settings
// 80mm thermal paper standard: ~72mm printable area = 280px at 96 DPI
export const DEFAULT_SETTINGS: ReceiptSettings = {
  width: 280,
  font: 'Merchant Copy',
  fontSize: 9,
  paperStyle: 'thermal',
  backgroundColor: '#ffffff',
  textColor: '#1a1a1a',
  showWatermark: false,
  watermarkText: 'TryReceiptMaker.com',
  currency: '$',
  currencyPosition: 'before',
  spacing: 'spaced',
};

// Legacy font-value aliases: keep receipts saved before a font was renamed
// rendering with the correct face. Back-compat only; not user-facing.
const LEGACY_FONT_ALIASES: Record<string, string> = {
  'Fake Receipt': 'Receipt Print',
};

export function normalizeFont(font: string): string {
  return LEGACY_FONT_ALIASES[font] ?? font;
}

// Default Header Config
export const DEFAULT_HEADER_CONFIG: HeaderConfig = {
  alignment: 'center',
  logoUrl: '',
  logoData: '',
  logoWidth: 120,
  logoHeight: 60,
  businessName: 'Business Name',
  businessNameFontSize: 18,
  addressLines: ['123 Main Street', 'City, State 12345', '(555) 123-4567'],
  showDivider: true,
  dividerStyle: 'dashed',
};

// Default Two Column Config
export const DEFAULT_TWO_COLUMN_CONFIG: TwoColumnConfig = {
  alignment: 'left',
  column1: [
    { id: uuidv4(), label: 'Trans:', value: '14005' },
    { id: uuidv4(), label: 'Register:', value: '1' },
    { id: uuidv4(), label: 'Store:', value: '4068' },
  ],
  column2: [],
  showDivider: false,
  dividerStyle: 'dashed',
};

// Default Items List Config
export const DEFAULT_ITEMS_LIST_CONFIG: ItemsListConfig = {
  alignment: 'left',
  showQuantity: true,
  quantityPosition: 'left',
  showHeader: true,
  showHeaderDivider: true,
  headerLabels: {
    quantity: 'Qty',
    item: 'Item',
    amount: 'Amount',
  },
  items: [
    { id: uuidv4(), quantity: '1', name: 'Item 1', price: '$29.99' },
    { id: uuidv4(), quantity: '2', name: 'Item 2', price: '$15.00' },
  ],
  showTopDivider: false,
  showDivider: false,
  dividerStyle: 'dashed',
};

// Default Totals Config
export const DEFAULT_TOTALS_CONFIG: TotalsConfig = {
  alignment: 'left',
  labelPosition: 50, // 50% = labels start at middle
  lines: [
    { id: uuidv4(), label: 'Subtotal', value: '59.99', isBold: false, isLarge: false },
    { id: uuidv4(), label: 'Tax', value: '5.40', isBold: false, isLarge: false },
    { id: uuidv4(), label: 'TOTAL', value: '65.39', isBold: false, isLarge: false },
  ],
  increaseTotalSize: false,
  totalSizeIncrease: 30,
  showDivider: false,
  dividerStyle: 'dashed',
};

// Default Payment Config
export const DEFAULT_PAYMENT_CONFIG: PaymentConfig = {
  alignment: 'left',
  paymentType: 'card',
  cardFields: [
    { id: uuidv4(), label: 'Card number', value: '**** **** **** 4922' },
    { id: uuidv4(), label: 'Card type', value: 'Debit' },
    { id: uuidv4(), label: 'Card entry', value: 'Chip' },
    { id: uuidv4(), label: 'Date/time', value: '11/20/2019 11:09 AM' },
    { id: uuidv4(), label: 'Reference #', value: '62845289260246240685C' },
    { id: uuidv4(), label: 'Status', value: 'APPROVED' },
  ],
  cashFields: [
    { id: uuidv4(), label: 'Cash', value: '$50.00' },
    { id: uuidv4(), label: 'Change', value: '$15.61' },
  ],
  showDivider: true,
  dividerStyle: 'dashed',
};

// Helper to format date consistently
export const formatDateTime = (date: Date = new Date()) => {
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

// Default Date Time Config - use static placeholder to avoid hydration mismatch
export const DEFAULT_DATETIME_CONFIG: DateTimeConfig = {
  alignment: 'center',
  format: 'MM/DD/YYYY, HH:mm:ss A',
  dateTime: '01/01/2025, 12:00:00 PM',
  showDivider: false,
  dividerStyle: 'dashed',
};

// Default Custom Message Config
export const DEFAULT_CUSTOM_MESSAGE_CONFIG: CustomMessageConfig = {
  alignment: 'center',
  message:
    'Thank you for your purchase! Returns accepted within 30 days with receipt.',
  fontSize: 11,
  showDivider: false,
  dividerStyle: 'dashed',
};

// Default Barcode Config
export const DEFAULT_BARCODE_CONFIG: BarcodeConfig = {
  alignment: 'center',
  type: 'code128',
  value: '1234567890123',
  showValue: true,
  width: 1.5,
  height: 50,
  showDivider: false,
  dividerStyle: 'dashed',
};

// Default Divider Config
export const DEFAULT_DIVIDER_CONFIG: DividerConfig = {
  style: 'dashed',
  spacing: 8,
};

// Factory function to create a new section with defaults
export function createSection(type: SectionType): ReceiptSection {
  const id = uuidv4();

  switch (type) {
    case 'header':
      return {
        id,
        type: 'header',
        enabled: true,
        config: { ...DEFAULT_HEADER_CONFIG },
      };
    case 'two-column':
      return {
        id,
        type: 'two-column',
        enabled: true,
        config: {
          ...DEFAULT_TWO_COLUMN_CONFIG,
          column1: DEFAULT_TWO_COLUMN_CONFIG.column1.map((r) => ({ ...r, id: uuidv4() })),
          column2: DEFAULT_TWO_COLUMN_CONFIG.column2.map((r) => ({ ...r, id: uuidv4() })),
        },
      };
    case 'items-list':
      return {
        id,
        type: 'items-list',
        enabled: true,
        config: {
          ...DEFAULT_ITEMS_LIST_CONFIG,
          items: DEFAULT_ITEMS_LIST_CONFIG.items.map((i) => ({ ...i, id: uuidv4() })),
        },
      };
    case 'totals':
      return {
        id,
        type: 'totals',
        enabled: true,
        config: {
          ...DEFAULT_TOTALS_CONFIG,
          lines: DEFAULT_TOTALS_CONFIG.lines.map((l) => ({ ...l, id: uuidv4() })),
        },
      };
    case 'payment':
      return {
        id,
        type: 'payment',
        enabled: true,
        config: {
          ...DEFAULT_PAYMENT_CONFIG,
          cardFields: DEFAULT_PAYMENT_CONFIG.cardFields?.map((f) => ({ ...f, id: uuidv4() })) || [],
          cashFields: DEFAULT_PAYMENT_CONFIG.cashFields?.map((f) => ({ ...f, id: uuidv4() })) || [],
        },
      };
    case 'datetime':
      return {
        id,
        type: 'datetime',
        enabled: true,
        config: { ...DEFAULT_DATETIME_CONFIG, dateTime: formatDateTime() },
      };
    case 'custom-message':
      return {
        id,
        type: 'custom-message',
        enabled: true,
        config: { ...DEFAULT_CUSTOM_MESSAGE_CONFIG },
      };
    case 'barcode':
      return {
        id,
        type: 'barcode',
        enabled: true,
        config: { ...DEFAULT_BARCODE_CONFIG },
      };
    case 'divider':
      return {
        id,
        type: 'divider',
        enabled: true,
        config: { ...DEFAULT_DIVIDER_CONFIG },
      };
  }
}

// Create a blank receipt template
export function createBlankTemplate(): ReceiptTemplate {
  return {
    id: uuidv4(),
    name: 'New Receipt',
    settings: { ...DEFAULT_SETTINGS },
    sections: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

// Create a template with common sections pre-populated
export function createDefaultTemplate(): ReceiptTemplate {
  return {
    id: uuidv4(),
    name: 'Default Receipt',
    settings: { ...DEFAULT_SETTINGS },
    sections: [
      createSection('header'),
      createSection('two-column'),
      createSection('items-list'),
      createSection('totals'),
      createSection('datetime'),
      createSection('payment'),
      createSection('custom-message'),
      createSection('barcode'),
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

